import { register } from '../auth/api.js';
import { getPendingRegistration, setPendingRegistration } from '../auth/storage.js';
import { navigate } from '../router.js';
import { errorBox, setError, setLoading, startRetryCountdown } from '../components/feedback.js';
import { logoMark } from '../components/branding.js';
import { logoSvg } from '../components/icons.js';

/** Step 1 of 2 — collect the e-mail and request a verification code. */
export function renderLogin(mount) {
  const pending = getPendingRegistration();

  // Clear any scroll lock a drawer/dialog may have left on <html>.
  document.documentElement.classList.remove('overflow-hidden');

  mount.className = 'min-h-dvh';
  mount.innerHTML = `
    <form id="login-form" class="mx-auto flex w-full max-w-sm flex-col px-4 py-4" novalidate>
      <div class="flex flex-col items-center gap-4 text-center py-8">
        ${logoSvg(64)}
        ${logoMark()}
      </div>

<div class="text-center">
  <h1 class="text-2xl font-bold tracking-tight text-neutral-950">
    Angielski w IT
  </h1>

  <p class="mt-1 text-xs font-medium text-neutral-400">
    Słuchaj &#x2022 Czytaj &#x2022 Powtarzaj
  </p>
</div>
    <div class="mt-8">
          <label for="email" class="mt-8 block text-sm font-semibold text-neutral-700 mb-1 px-1">Dołącz do darmowej aplikacji:</label>
          <input
            id="email" name="email" type="email" required autocomplete="email" inputmode="email" placeholder="Adres e-mail"
            class="w-full rounded-xl border-2 border-neutral-300 font-medium bg-white px-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 transition focus:border-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-700"
          />
          ${errorBox('login-error')}
          </div>
      <button
        id="login-submit" type="submit"
        class="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-700 px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-neutral-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-neutral-900/20 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span class="submit-label">Wyślij kod</span>
      </button>
      <p class="mt-4 text-center text-sm leading-relaxed text-neutral-500">
        Korzystając z aplikacji akceptujesz
        <a href="#/regulamin" class="font-medium text-neutral-600 underline underline-offset-2 transition hover:text-neutral-900">regulamin</a>
        oraz
        <a href="#/polityka-prywatnosci" class="font-medium text-neutral-600 underline underline-offset-2 transition hover:text-neutral-900">politykę prywatności</a>.
      </p>
    </form>`;

  const form = mount.querySelector('#login-form');
  const emailInput = mount.querySelector('#email');
  const errorEl = mount.querySelector('#login-error');
  const submit = mount.querySelector('#login-submit');

  if (pending?.email) emailInput.value = pending.email;
  emailInput.focus();

  /*
   * Rate limit (429 from /register — one code per address per 5 minutes, plus a
   * per-IP quota). The error box shows the backend's message plus a ticking
   * "next one in 1 min. 23 sek." line, and the submit button stays disabled for
   * as long as it counts: pressing it again can only collect the same 429, since
   * the backend peeks at the limiter without consuming a token and so never
   * pushes the retry moment further out.
   *
   * Deliberately in-memory only — a reload just tries again and gets a fresh,
   * current number from the API.
   */
  let stopCountdown = null;
  let blockedEmail = null;

  const releaseLimit = () => {
    stopCountdown?.();
    stopCountdown = null;
    blockedEmail = null;
    submit.disabled = false;
    setError(errorEl, null);
  };

  // The per-address quota is keyed on the address, so a different one may well go
  // through — typing one releases the button instead of holding a fresh address
  // hostage to the previous one's five minutes.
  emailInput.addEventListener('input', () => {
    if (blockedEmail !== null && emailInput.value.trim() !== blockedEmail) releaseLimit();
  });

  // The router swaps the mount out from under the view, so the countdown's
  // interval would otherwise outlive the screen it belongs to.
  window.addEventListener(
    'hashchange',
    () => {
      stopCountdown?.();
      stopCountdown = null;
    },
    { once: true }
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submit.disabled) return;
    stopCountdown?.();
    stopCountdown = null;
    blockedEmail = null;
    setError(errorEl, null);

    const email = emailInput.value.trim();
    if (!email) {
      setError(errorEl, { message: 'Podaj adres e-mail.' });
      return;
    }

    setLoading(submit, true, 'Wysyłanie…');
    let limited = null;
    try {
      const result = await register(email);
      setPendingRegistration({ jwt: result.jwt, email });
      navigate('/verify');
    } catch (error) {
      setError(errorEl, error);
      if (error?.status === 429 && error.retryAfter > 0) limited = error.retryAfter;
    } finally {
      // Re-enable first — `setLoading` owns `disabled` — then hold the button for
      // the countdown if this was a 429.
      setLoading(submit, false);
      if (limited !== null) {
        blockedEmail = email;
        submit.disabled = true;
        stopCountdown = startRetryCountdown(errorEl, limited, { onExpire: releaseLimit });
      }
    }
  });
}
